import { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Tag, Plus, Edit2, Trash2, Save, X, Calendar, Percent, Users, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface DiscountCode {
  id: string;
  code: string;
  type: "percentage" | "fixed";
  value: number;
  description: string;
  isActive: boolean;
  usageLimit?: number;
  usedCount: number;
  validFrom?: Date;
  validUntil?: Date;
  applicableTo: "all" | "subscription" | "courses";
  createdAt: Date;
}

export default function DiscountManagement() {
  const navigate = useNavigate();
  const [discounts, setDiscounts] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    code: "",
    type: "percentage" as "percentage" | "fixed",
    value: 0,
    description: "",
    isActive: true,
    usageLimit: 0,
    validFrom: "",
    validUntil: "",
    applicableTo: "all" as "all" | "subscription" | "courses",
  });

  useEffect(() => {
    loadDiscounts();
  }, []);

  const loadDiscounts = async () => {
    try {
      // Load from subscription_discounts collection
      const q = query(collection(db, "subscription_discounts"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        validFrom: doc.data().validFrom?.toDate(),
        validUntil: doc.data().validUntil?.toDate(),
        usedCount: doc.data().usedCount || 0,
      })) as DiscountCode[];
      setDiscounts(data);
    } catch (error) {
      console.error("Error loading discounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const discountData = {
        code: formData.code.toUpperCase().trim(),
        type: formData.type,
        value: Number(formData.value),
        description: formData.description,
        isActive: formData.isActive,
        usageLimit: formData.usageLimit > 0 ? Number(formData.usageLimit) : null,
        validFrom: formData.validFrom ? new Date(formData.validFrom) : null,
        validUntil: formData.validUntil ? new Date(formData.validUntil) : null,
        applicableTo: formData.applicableTo,
        usedCount: 0,
      };

      if (editingId) {
        await updateDoc(doc(db, "subscription_discounts", editingId), {
          ...discountData,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, "subscription_discounts"), {
          ...discountData,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      loadDiscounts();
    } catch (error) {
      console.error("Error saving discount:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (discount: DiscountCode) => {
    setFormData({
      code: discount.code,
      type: discount.type,
      value: discount.value,
      description: discount.description,
      isActive: discount.isActive,
      usageLimit: discount.usageLimit || 0,
      validFrom: discount.validFrom ? discount.validFrom.toISOString().split("T")[0] : "",
      validUntil: discount.validUntil ? discount.validUntil.toISOString().split("T")[0] : "",
      applicableTo: discount.applicableTo,
    });
    setEditingId(discount.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("ต้องการลบโค้ดส่วนลดนี้หรือไม่?")) return;

    try {
      await deleteDoc(doc(db, "subscription_discounts", id));
      loadDiscounts();
    } catch (error) {
      console.error("Error deleting discount:", error);
      alert("เกิดข้อผิดพลาดในการลบ");
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      type: "percentage",
      value: 0,
      description: "",
      isActive: true,
      usageLimit: 0,
      validFrom: "",
      validUntil: "",
      applicableTo: "all",
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/discounts')}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">กลับไปหน้าแดชบอร์ด</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
                <Tag className="w-10 h-10 text-blue-600" />
                จัดการโค้ดส่วนลด
              </h1>
              <p className="text-gray-600 mt-2">สร้างและจัดการโค้ดส่วนลดสำหรับ Subscription และคอร์สเรียน</p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              <Plus className="w-5 h-5" />
              สร้างโค้ดใหม่
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {editingId ? "แก้ไขโค้ดส่วนลด" : "สร้างโค้ดส่วนลดใหม่"}
                  </h2>
                  <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Code */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">รหัสส่วนลด *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="เช่น NEWYEAR2026"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">คำอธิบาย</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="เช่น ส่วนลดปีใหม่"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Type and Value */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">ประเภท *</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as "percentage" | "fixed" })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="percentage">เปอร์เซ็นต์ (%)</option>
                        <option value="fixed">จำนวนเงิน (฿)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        มูลค่า * {formData.type === "percentage" ? "(%)" : "(฿)"}
                      </label>
                      <input
                        type="number"
                        value={formData.value}
                        onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
                        min="0"
                        max={formData.type === "percentage" ? "100" : undefined}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Applicable To */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">ใช้ได้กับ</label>
                    <select
                      value={formData.applicableTo}
                      onChange={(e) => setFormData({ ...formData, applicableTo: e.target.value as any })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">ทั้งหมด</option>
                      <option value="subscription">Subscription เท่านั้น</option>
                      <option value="courses">คอร์สเรียนเท่านั้น</option>
                    </select>
                  </div>

                  {/* Usage Limit */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      จำกัดการใช้งาน (0 = ไม่จำกัด)
                    </label>
                    <input
                      type="number"
                      value={formData.usageLimit}
                      onChange={(e) => setFormData({ ...formData, usageLimit: Number(e.target.value) })}
                      min="0"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Valid Period */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">เริ่มใช้งาน</label>
                      <input
                        type="date"
                        value={formData.validFrom}
                        onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">หมดอายุ</label>
                      <input
                        type="date"
                        value={formData.validUntil}
                        onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="isActive" className="text-sm font-semibold text-gray-700">
                      เปิดใช้งาน
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <Save className="w-5 h-5" />
                      {saving ? "กำลังบันทึก..." : editingId ? "บันทึกการแก้ไข" : "สร้างโค้ด"}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                    >
                      ยกเลิก
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Discounts List */}
        <div className="grid gap-4">
          {discounts.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">ยังไม่มีโค้ดส่วนลด</p>
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 text-blue-600 font-semibold hover:underline"
              >
                สร้างโค้ดแรกของคุณ
              </button>
            </div>
          ) : (
            discounts.map((discount) => (
              <div
                key={discount.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-bold text-blue-600">{discount.code}</span>
                      {discount.isActive ? (
                        <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" />
                          ใช้งานได้
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold">
                          <XCircle className="w-4 h-4" />
                          ปิดใช้งาน
                        </span>
                      )}
                    </div>

                    {discount.description && (
                      <p className="text-gray-600 mb-3">{discount.description}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Percent className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold">
                          {discount.type === "percentage"
                            ? `${discount.value}%`
                            : `฿${discount.value.toLocaleString()}`}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span>
                          ใช้แล้ว: {discount.usedCount}
                          {discount.usageLimit ? ` / ${discount.usageLimit}` : " (ไม่จำกัด)"}
                        </span>
                      </div>

                      {discount.validUntil && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span>
                            หมดอายุ: {discount.validUntil.toLocaleDateString("th-TH")}
                          </span>
                        </div>
                      )}

                      <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {discount.applicableTo === "all"
                          ? "ทั้งหมด"
                          : discount.applicableTo === "subscription"
                          ? "Subscription"
                          : "คอร์สเรียน"}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(discount)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="แก้ไข"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(discount.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="ลบ"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
